import React, { useState } from 'react';
import { Shield, Scale, Building2, HeartPulse, Truck, Home, ChevronRight, Lock, Archive, Layers } from 'lucide-react';

const FAMILY_ICONS = {
  general_liability: Shield,
  professional_management_liability: Scale,
  commercial_property_pack: Building2,
  accident_health: HeartPulse,
  motor: Truck,
  residential_property: Home,
};

const RESIDENTIAL = new Set(['home_contents', 'residential_landlords']);

const STATE_STYLE = {
  active: { label: 'Active', color: '#1D9E75', bg: 'rgba(29,158,117,0.1)' },
  disabled: { label: 'Disabled — configuration kept', color: '#5C6B7A', bg: 'rgba(92,107,122,0.12)' },
  'run-off': { label: 'Run-off', color: '#B27516', bg: 'rgba(232,160,32,0.14)' },
};

export function StateChip({ state, compact = false }) {
  const s = STATE_STYLE[state] || STATE_STYLE.active;
  return (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm whitespace-nowrap" style={{ color: s.color, background: s.bg }} data-testid={`state-chip-${state}`}>
      {compact && state === 'disabled' ? 'Disabled' : s.label}
    </span>
  );
}

function FamilyTile({ fam, selected, onClick }) {
  const Icon = FAMILY_ICONS[fam.id] || Shield;
  const none = fam.total === 0;
  const offered = fam.quotable_count;
  return (
    <button type="button" data-testid={`family-${fam.id}`} onClick={onClick} disabled={none}
      className={`text-left p-5 rounded-sm border bg-white transition-all ${none ? 'opacity-50 cursor-not-allowed' : 'hover:-translate-y-[1px]'}`}
      style={{ borderColor: selected ? '#1A4C7C' : 'rgba(136,150,168,0.2)', boxShadow: selected ? '0 0 0 1px #1A4C7C' : 'none' }}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: selected ? 'rgba(26,76,124,0.1)' : 'rgba(0,180,216,0.1)' }}>
          <Icon className="w-5 h-5" style={{ color: selected ? '#1A4C7C' : '#00B4D8' }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-mono uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Family {fam.number} · {fam.engine_label} · {fam.trigger_label}</p>
          <p className="text-sm font-semibold mt-0.5" style={{ color: '#0D1B2A' }}>{fam.name}</p>
        </div>
        <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${selected ? 'rotate-90' : ''}`} style={{ color: '#5C6B7A' }} />
      </div>
      <p className="text-xs mt-3 leading-relaxed" style={{ color: '#5C6B7A' }}>{fam.description}</p>
      <p className="text-[11px] mt-3 font-medium" style={{ color: offered ? '#1D9E75' : '#5C6B7A' }} data-testid={`family-${fam.id}-quotable`}>
        {offered ? `${offered} of ${fam.total} variant${fam.total === 1 ? '' : 's'} quotable` : none ? 'Nothing offered' : `0 of ${fam.total} quotable`}
      </p>
    </button>
  );
}

function VariantCard({ v, onSelect }) {
  const blocked = !v.quotable;
  const reason = (v.blockers || [])[0]?.detail || v.state_basis || 'not available for new business';
  const Icon = v.state === 'run-off' ? Archive : Lock;
  return (
    <button type="button" key={v.class_id} data-testid={`class-${v.class_id}`} disabled={blocked}
      onClick={() => !blocked && onSelect(v.class_id)} title={blocked ? reason : undefined}
      className={`text-left p-4 rounded-sm border bg-white transition-all ${blocked ? 'cursor-not-allowed' : 'hover:border-[#00B4D8] hover:-translate-y-[1px]'}`}
      style={{ borderColor: blocked ? 'rgba(136,150,168,0.25)' : 'rgba(136,150,168,0.2)', opacity: blocked ? 0.75 : 1 }}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold truncate" style={{ color: blocked ? '#5C6B7A' : '#0D1B2A' }}>{v.label}</p>
        <StateChip state={v.state} compact />
      </div>
      <p className="text-[10px] mt-0.5" style={{ color: '#5C6B7A' }}>{v.name}{v.wording_version ? ` · wording v${v.wording_version}` : ''}</p>
      {!blocked && RESIDENTIAL.has(v.class_id) && <p className="mt-1 text-[9px] font-semibold uppercase tracking-wider" style={{ color: '#1A4C7C' }} data-testid={`class-journey-${v.class_id}`}>Address-first journey · ≤ 20 typed inputs</p>}
      {blocked ? (
        <p className="mt-2 text-[10px] leading-snug flex items-start gap-1" style={{ color: v.state === 'active' ? '#E24B4A' : '#5C6B7A' }} data-testid={`class-blocked-${v.class_id}`}>
          <Icon className="w-3 h-3 mt-[1px] shrink-0" />
          <span>{v.state === 'run-off' && v.runoff ? `Run-off — renewals to ${v.runoff.notice_end}. ` : ''}{reason}</span>
        </p>
      ) : (
        <div className="mt-2 flex gap-1 flex-wrap">
          {(v.sections || []).slice(0, 6).map(s => (
            <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-sm capitalize" style={{ background: 'rgba(136,150,168,0.1)', color: '#5C6B7A' }}>{s.replace(/_/g, ' ')}</span>
          ))}
        </div>
      )}
    </button>
  );
}

export default function FamilyPicker({ families, onSelect, isStaff }) {
  const [open, setOpen] = useState(null);
  const fam = families.find(f => f.id === open);
  return (
    <div className="space-y-4" data-testid="family-picker">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {families.map(f => (
          <FamilyTile key={f.id} fam={f} selected={open === f.id} onClick={() => setOpen(open === f.id ? null : f.id)} />
        ))}
      </div>
      {fam && (
        <div className="rounded-sm p-4" style={{ background: '#F5F7FA', border: '1px solid rgba(136,150,168,0.2)' }} data-testid={`family-variants-${fam.id}`}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#1A4C7C' }}>{fam.name} — choose a variant</p>
            {isStaff && <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Staff view: variants a broker is not offered are shown with the gap named</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {fam.variants.map(v => <VariantCard key={v.class_id} v={v} onSelect={onSelect} />)}
          </div>
          {(fam.combined_forms || []).length > 0 && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="combined-forms">
              {fam.combined_forms.map(c => (
                <button type="button" key={c.id} data-testid={`class-${c.id}`} disabled={!c.quotable}
                  onClick={() => c.quotable && onSelect(c.id)} title={!c.quotable ? (c.blockers[0]?.detail || 'not available') : undefined}
                  className={`text-left p-4 rounded-sm border transition-all ${c.quotable ? 'hover:-translate-y-[1px]' : 'cursor-not-allowed opacity-75'}`}
                  style={{ borderColor: '#1A4C7C', background: 'rgba(26,76,124,0.04)' }}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>{c.label}</p>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm flex items-center gap-1" style={{ color: '#1A4C7C', background: 'rgba(26,76,124,0.1)' }}><Layers className="w-3 h-3" /> Combined form</span>
                  </div>
                  <p className="text-[11px] mt-1 leading-snug" style={{ color: '#5C6B7A' }}>{c.description}</p>
                  {!c.quotable && <p className="mt-2 text-[10px]" style={{ color: '#E24B4A' }}>{c.blockers[0]?.detail || 'One leg is not quotable'}</p>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
